import { useContext, useEffect } from 'react'
import styled from 'styled-components'

import { IManual } from '../../utils/types'
import useManualMenuNav from '../../hooks/floating-manual-nav/manual-nav-menu'
import ManualMenu from './manual-nav/manual-menu'
import ManualShare from './manual-nav/manual-share'
import useManualShare from '../../hooks/floating-manual-nav/manual-nav-share'
import { useDetectClickOutside } from 'react-detect-click-outside'
import { ThemeContext } from '../../contexts/ThemeContext'

const FloatingManualNavContainer = styled.div`
	position: fixed;
	bottom: 20px;
	left: 50%;
	transform: translateX(-50%);
	transition: all 0.8s ease-in-out;
	z-index: 10;
`

const FloatingManualNavBox = styled.div<{ isLightTheme: boolean }>`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	padding: 16px 40px;
	background: var(--background-color);
	box-shadow: ${(props) =>
		props.isLightTheme
			? '-4px 8px 48px rgba(0, 0, 0, 0.1), 8px 0px 100px rgba(0, 0, 0, 0.02)'
			: '-4px 8px 48px rgba(255, 255, 255, 0.1), 8px 0px 100px rgba(255, 255, 255, 0.02)'};

	border-radius: 24px;

	height: 56px;

	& > span {
		height: 24px;
		border: 1px solid #e8e8e8;
		margin: 0 28px;
	}
`

const StyledSVG = styled.svg`
	path,
	g > path {
		stroke: var(--svg-stroke-color);
		fill: var(--svg-fill-color);
	}
`

interface IFloatingNavStyleProps {
	manual: IManual
	isShown: boolean
}

const FloatingManualNav = ({ manual, isShown }: IFloatingNavStyleProps) => {
	const { isMenuShown, toggleMenuShow, menuContent, closeMenu } = useManualMenuNav({ manual })
	const { isManualShareShown, toggleNavShareShow, closeNavShare } = useManualShare()

	const { theme, toggleTheme } = useContext(ThemeContext)

	const closeAllMenu = () => {
		closeMenu()
		closeNavShare()
	}

	const ref = useDetectClickOutside({ onTriggered: closeAllMenu })

	const handleManualMenuBtnClick = () => {
		closeNavShare()
		toggleMenuShow()
	}

	const handleManualNavShareBtnClick = () => {
		closeMenu()
		toggleNavShareShow()
	}

	useEffect(() => {
		const floatingManualNav = document.querySelector('.floating-manual-nav') as HTMLDivElement | null

		if (floatingManualNav != null) {
			if (isShown) {
				floatingManualNav.style.transform = 'translateX(-50%)'
			} else {
				floatingManualNav.style.transform = 'translateX(-50%) translateY(150px)'
			}
		}
	}, [isShown])

	return (
		<FloatingManualNavContainer className='floating-manual-nav' ref={ref}>
			<FloatingManualNavBox isLightTheme={theme === 'light'}>
				{theme === 'light' ? (
					<svg onClick={toggleTheme} width='24' height='24' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'>
						<path
							d='M23.7016 16.6906C21.9837 17.1724 20.1685 17.1881 18.4424 16.7364C16.7164 16.2846 15.1417 15.3815 13.8801 14.1199C12.6185 12.8583 11.7154 11.2836 11.2636 9.55757C10.8119 7.83154 10.8277 6.01635 11.3094 4.29843C9.61567 4.76985 8.07503 5.6768 6.84092 6.92894C5.6068 8.18107 4.72228 9.7347 4.27547 11.4351C3.82865 13.1354 3.83513 14.9232 4.29426 16.6203C4.75339 18.3174 5.64915 19.8645 6.89231 21.1077C8.13547 22.3509 9.68264 23.2466 11.3797 23.7057C13.0768 24.1649 14.8646 24.1714 16.5649 23.7245C18.2653 23.2777 19.8189 22.3932 21.0711 21.1591C22.3232 19.925 23.2302 18.3843 23.7016 16.6906V16.6906Z'
							stroke='var(--svg-stroke-color)'
							strokeWidth='1.5'
							strokeLinecap='round'
							strokeLinejoin='round'
						/>
					</svg>
				) : (
					<svg
						onClick={toggleTheme}
						width='24px'
						height='24px'
						viewBox='0 0 24 24'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<g id='SVGRepo_bgCarrier' strokeWidth='0'></g>
						<g id='SVGRepo_tracerCarrier' strokeLinecap='round' strokeLinejoin='round'></g>
						<g id='SVGRepo_iconCarrier'>
							{' '}
							<circle cx='12' cy='12' r='5' stroke='#ffffff' strokeWidth='1.5'></circle>{' '}
							<path d='M12 2V4' stroke='#ffffff' strokeWidth='1.5' strokeLinecap='round'></path>{' '}
							<path d='M12 20V22' stroke='#ffffff' strokeWidth='1.5' strokeLinecap='round'></path>{' '}
							<path d='M4 12L2 12' stroke='#ffffff' strokeWidth='1.5' strokeLinecap='round'></path>{' '}
							<path d='M22 12L20 12' stroke='#ffffff' strokeWidth='1.5' strokeLinecap='round'></path>{' '}
							<path d='M19.7778 4.22266L17.5558 6.25424' stroke='#ffffff' strokeWidth='1.5' strokeLinecap='round'></path>{' '}
							<path d='M4.22217 4.22266L6.44418 6.25424' stroke='#ffffff' strokeWidth='1.5' strokeLinecap='round'></path>{' '}
							<path d='M6.44434 17.5557L4.22211 19.7779' stroke='#ffffff' strokeWidth='1.5' strokeLinecap='round'></path>{' '}
							<path d='M19.7778 19.7773L17.5558 17.5551' stroke='#ffffff' strokeWidth='1.5' strokeLinecap='round'></path>{' '}
						</g>
					</svg>
				)}

				<span></span>

				<StyledSVG
					onClick={handleManualMenuBtnClick}
					width='24'
					height='24'
					viewBox='0 0 24 24'
					fill='none'
					xmlns='http://www.w3.org/2000/svg'
				>
					<path d='M10.5 4.5H4.5V10.5H10.5V4.5Z' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
					<path d='M19.5 4.5H13.5V10.5H19.5V4.5Z' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
					<path d='M10.5 13.5H4.5V19.5H10.5V13.5Z' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
					<path d='M19.5 13.5H13.5V19.5H19.5V13.5Z' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
				</StyledSVG>
				<ManualMenu isMenuShown={isMenuShown} closeMenu={closeMenu} menuContent={menuContent} />

				<span></span>

				<a href='https://nlwc.church/audio-messages/' target='_blank' rel='noreferrer'>
					<StyledSVG width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
						<path
							d='M20.522 7.22812C21.1496 7.85425 21.6475 8.59802 21.9872 9.41685C22.3269 10.2357 22.5018 11.1135 22.5018 12C22.5018 12.8865 22.3269 13.7643 21.9872 14.5831C21.6475 15.402 21.1496 16.1457 20.522 16.7719'
							strokeWidth='1.5'
							strokeLinecap='round'
							strokeLinejoin='round'
						/>
						<path
							d='M7.5 15.75H3C2.80109 15.75 2.61032 15.671 2.46967 15.5303C2.32902 15.3897 2.25 15.1989 2.25 15V9C2.25 8.80109 2.32902 8.61032 2.46967 8.46967C2.61032 8.32902 2.80109 8.25 3 8.25H7.5L14.25 3V21L7.5 15.75Z'
							strokeWidth='1.5'
							strokeLinecap='round'
							strokeLinejoin='round'
						/>
						<path d='M7.5 8.25V15.75' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
						<path
							d='M17.8687 9.88126C18.1476 10.1591 18.3689 10.4893 18.5199 10.8529C18.6709 11.2165 18.7487 11.6063 18.7487 12C18.7487 12.3937 18.6709 12.7835 18.5199 13.1471C18.3689 13.5107 18.1476 13.8409 17.8687 14.1188'
							strokeWidth='1.5'
							strokeLinecap='round'
							strokeLinejoin='round'
						/>
					</StyledSVG>
				</a>

				<span></span>

				<StyledSVG
					onClick={handleManualNavShareBtnClick}
					width='24'
					height='24'
					viewBox='0 0 24 24'
					fill='none'
					xmlns='http://www.w3.org/2000/svg'
				>
					<path d='M16.5 14.25L21 9.75L16.5 5.25' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
					<path
						d='M18 20.25H3.75C3.55109 20.25 3.36032 20.171 3.21967 20.0303C3.07902 19.8897 3 19.6989 3 19.5V8.25'
						strokeWidth='1.5'
						strokeLinecap='round'
						strokeLinejoin='round'
					/>
					<path
						d='M7.03125 16.5C7.53172 14.5681 8.65943 12.8571 10.2374 11.6354C11.8154 10.4138 13.7544 9.7506 15.75 9.75H21'
						strokeWidth='1.5'
						strokeLinecap='round'
						strokeLinejoin='round'
					/>
				</StyledSVG>

				<ManualShare
					isManualShareBoxShown={isManualShareShown}
					date={manual.manual_date}
					id={manual.id}
					manualTitle={manual.topic}
				/>
			</FloatingManualNavBox>
		</FloatingManualNavContainer>
	)
}

export default FloatingManualNav
